import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarPlus, AlertCircle, Inbox } from 'lucide-react'
import { useGetInterviewSchedule } from '../hooks/backend/interviews'
import { useInterviewDecisions } from '../hooks/useInterviewDecisions'
import { useManager } from '../hooks/useManager'
import { useAccess } from '../hooks/useAccess'
import { Spinner } from '../lib/shadcn/spinner'
import { ManagerBanner } from '../components/interviews/ManagerBanner'
import { WorklistCard } from '../components/interviews/WorklistCard'
import type { OutcomeAction } from '../components/interviews/DecisionActions'
import { groupByInterviewDay, type WorklistRow } from '../utils/interviews'

// The Interviews tab, 29 October 2026. Was Upcoming Interviews, which started
// tomorrow, so the day's own interviews were only on Today and mixed with
// overdue ones. Now it is today and every day after, grouped by day, and each
// card carries the progress strip so you can see who an interview is with and
// whether they have scored, without opening it. The route stays /upcoming so
// nobody's bookmark breaks.

const NOBODY = 'Nobody assigned'

// The people an interview is with, by first name, as the progress strip names
// them. An unassigned interview is its own group, because that is a problem to
// fix rather than a person to ask.
function interviewersOf(item: WorklistRow): string[] {
  const p = item.progress
  if (!p || p.unassigned) return [NOBODY]
  const names = p.scorers.filter((s) => s.assigned !== false).map((s) => s.short_name)
  return names.length ? names : [NOBODY]
}

export default function UpcomingInterviews() {
  const { linked, loading: managerLoading } = useManager()
  const { canAct, isViewer, caption } = useAccess()
  const { data, loading, error, trigger } = useGetInterviewSchedule()
  const { submit, busyId } = useInterviewDecisions()
  const [who, setWho] = useState<string | null>(null)

  const load = useCallback(() => {
    trigger({}, { skipCache: true })
  }, [trigger])

  useEffect(() => {
    load()
  }, [load])

  const items = (data as WorklistRow[] | undefined) ?? []

  const people = useMemo(() => {
    const counts = new Map<string, number>()
    for (const item of items) for (const n of interviewersOf(item)) counts.set(n, (counts.get(n) ?? 0) + 1)
    return [...counts.entries()].sort((a, b) => (a[0] === NOBODY ? 1 : b[0] === NOBODY ? -1 : a[0].localeCompare(b[0])))
  }, [items])

  // A filter on someone who has dropped off the list would show an empty page
  // with no way to tell why, so it falls back to everybody.
  const activeWho = who && people.some(([n]) => n === who) ? who : null
  const shown = activeWho ? items.filter((i) => interviewersOf(i).includes(activeWho)) : items
  const days = groupByInterviewDay(shown)

  // Viewers can never act; managers/admins can act once linked to a person record.
  const cardsCanAct = canAct && linked

  const handleSubmit = useCallback(
    async (interviewId: string, action: OutcomeAction, notes: string | null) => {
      await submit(interviewId, action, notes, load)
    },
    [submit, load],
  )

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-[hsl(var(--brand))]" />
            <h2 className="text-xl font-semibold text-foreground">Interviews</h2>
          </div>
          <span className="text-sm text-muted-foreground tabular-nums">
            {loading ? '…' : `${items.length} interview${items.length === 1 ? '' : 's'} today and upcoming`}
          </span>
        </div>

        <p className="text-sm text-muted-foreground">
          Every open interview from today on, by day, with who it is with and whether they have scored. Earlier
          interviews still waiting for an outcome are on Today.
        </p>

        {caption && <p className="text-sm text-muted-foreground">{caption}</p>}

        {/* Managers who aren't linked can't act. Viewers are read-only by design, so
            don't nag them about a manager link. */}
        {!isViewer && !managerLoading && !linked && <ManagerBanner />}

        {people.length > 1 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by interviewer">
            <FilterButton active={activeWho === null} onClick={() => setWho(null)}>
              Everyone {items.length}
            </FilterButton>
            {people.map(([name, count]) => (
              <FilterButton key={name} active={activeWho === name} onClick={() => setWho(name)}>
                {name} {count}
              </FilterButton>
            ))}
          </div>
        )}

        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : loading && items.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading interviews…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
            <Inbox className="h-8 w-8 opacity-40" />
            <p className="text-sm font-medium text-foreground">No interviews today or coming up</p>
            <p className="text-sm">Interviews booked for today or a later date will appear here.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {days.map((day) => (
              <section key={day.key} className="space-y-3">
                <div className="flex items-baseline justify-between gap-2 border-b border-border pb-1">
                  <h3
                    className={`text-sm font-semibold ${day.isToday ? 'text-[hsl(var(--brand))]' : 'text-foreground'}`}
                  >
                    {day.label}
                  </h3>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {day.items.length} interview{day.items.length === 1 ? '' : 's'}
                  </span>
                </div>
                {day.items.map((item) => (
                  <WorklistCard
                    key={item.interview_id}
                    item={item}
                    canAct={cardsCanAct}
                    busy={busyId === item.interview_id}
                    onSubmit={handleSubmit}
                    onChanged={load}
                    isDuplicate={item.likely_duplicate === true}
                  />
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs font-medium tabular-nums transition-colors ${
        active
          ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
          : 'border-border bg-card text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}
